import { MutationCache, QueryCache, QueryClient, QueryKey } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { MapAxiosErrorToRocketError, shouldRetryQueryOnError } from './errors';

interface CreateQueryClientOptions {
  mapAxiosErrorToRocketError: MapAxiosErrorToRocketError;
  resolveInvalidationTemplates?: (
    templates: readonly (readonly unknown[])[],
    variables: unknown
  ) => QueryKey[];
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      rel?: string;
      siteId?: string | number;
      invalidateTemplates?: readonly (readonly unknown[])[];
    };
  }
}

const matchesPathPrefix = (queryKey: QueryKey, target: string): boolean => {
  const [path] = queryKey;
  return typeof path === 'string' && (path === target || path.startsWith(`${target}/`));
};

export function createQueryClient({
  mapAxiosErrorToRocketError,
  resolveInvalidationTemplates,
}: CreateQueryClientOptions) {
  // async is intentional: causes the exception to be an unhandled promise rejection,
  // which will be caught by ErrorHandler instead of react-query's internal error handling
  const handleResponseError = async function (
    error: unknown,
    type: 'mutation' | 'query',
    meta?: Record<string, unknown>
  ) {
    if (error instanceof AxiosError) {
      const rocketError = mapAxiosErrorToRocketError(error, type, meta);
      if (rocketError) {
        throw rocketError;
      }
    }
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: shouldRetryQueryOnError,
        staleTime: 30_000,
        gcTime: 5 * 60_000,
      },
      mutations: {
        onError: (error: Error) => {
          handleResponseError(error, 'mutation');
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        handleResponseError(error, 'query', query.meta);
      },
    }),
    mutationCache: new MutationCache({
      onSuccess: async (_data, variables, _context, mutation) => {
        const invalidateTemplates = mutation.meta?.invalidateTemplates;
        const resolvedTemplateKeys =
          invalidateTemplates?.length && resolveInvalidationTemplates
            ? resolveInvalidationTemplates(invalidateTemplates, variables)
            : [];
        await Promise.all(
          resolvedTemplateKeys.map(queryKey => {
            const [target] = queryKey;
            return typeof target === 'string'
              ? queryClient.invalidateQueries({
                  predicate: query => matchesPathPrefix(query.queryKey, target),
                })
              : queryClient.invalidateQueries({ queryKey });
          })
        );
      },
    }),
  });

  return queryClient;
}
